/**
 * AbilityDataAdapter — Bridge between TOML-generated ability data and the AbilityRegistry.
 *
 * Provides lookup functions for ability balance data loaded from content/abilities/*.toml (h2kdb).
 * AbilityBuilder.withTomlData() is the primary consumer — it reads all numeric values
 * from TOML at build time.
 *
 * The legacy applyTomlData() late-patching function has been removed because
 * all ability files now use withTomlData() directly.
 */

import {
  ABILITY_DATA,
  type AbilityDataDef,
  getAbilityData,
  getAbilitiesForClass,
} from './generated/abilities.gen';

export type { AbilityDataDef };
export { ABILITY_DATA, getAbilityData, getAbilitiesForClass };

import { ABILITY_CONSTANTS } from '../constants/AbilityConstants';

/**
 * Calculate ability damage from TOML data + player stats.
 * Eliminates hardcoded base damage / scale factor in individual ability files.
 *
 * Formula: (baseDamage) + (stat * scaleFactor) + (level * levelScaling)
 */
export function calcAbilityDamage(
  abilityId: string,
  stat: number,
  level: number,
  opts?: { levelScaling?: number },
): number {
  const data = getAbilityData(abilityId);
  const baseDamage = data?.baseDamage ?? ABILITY_CONSTANTS.FALLBACK_BASE_DAMAGE;
  const scaleFactor = data?.scaleFactor ?? ABILITY_CONSTANTS.FALLBACK_SCALE_FACTOR;
  const levelScale = opts?.levelScaling ?? ABILITY_CONSTANTS.LEVEL_SCALING_MELEE;
  return Math.floor(baseDamage + stat * scaleFactor + level * levelScale);
}
